import { useState } from "react";
import { ApiError } from "@/lib/api.js";
import { useTenant } from "@/lib/tenant-context.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Select } from "@/components/ui/select.js";
import { Textarea } from "@/components/ui/textarea.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { useToast } from "@/components/ui/toast.js";
import type { Customer, Resource, Service } from "@/lib/types.js";

/**
 * Staff-direct booking form (Flow C). Calls POST /api/v1/appointments. On 409
 * slot_taken, surfaces the structured details so staff can pick a different time
 * or escalate to walk-in override.
 */
export function BookingDialog({
  open,
  onOpenChange,
  customers,
  services,
  resources,
  defaultStartsAt,
  defaultResourceId,
  onCreated
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  customers: Customer[];
  services: Service[];
  resources: Resource[];
  defaultStartsAt?: string;
  defaultResourceId?: string;
  onCreated?: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [conflictBanner, setConflictBanner] = useState<string | null>(null);
  const [form, setForm] = useState({
    customer_id: customers[0]?.id ?? "",
    service_id: services[0]?.id ?? "",
    primary_resource_id: defaultResourceId ?? resources[0]?.id ?? "",
    starts_at: defaultStartsAt ?? "",
    notes: ""
  });

  async function submit(): Promise<void> {
    if (!form.customer_id || !form.service_id || !form.primary_resource_id || !form.starts_at) {
      toast({ title: "Missing fields", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    setConflictBanner(null);
    const picked = customers.find((c) => c.id === form.customer_id);
    try {
      await api.createStaffAppointment({
        customer_id: form.customer_id,
        // Snapshot the picked customer's display fields onto the appointment
        // so the staff grid never has to round-trip back to the CRM for a name.
        customer_name: picked?.name,
        customer_phone: picked?.phone_e164 ?? undefined,
        service_id: form.service_id,
        primary_resource_id: form.primary_resource_id,
        starts_at: form.starts_at,
        notes: form.notes || undefined
      });
      toast({ title: "Appointment created", variant: "success" });
      onOpenChange(false);
      onCreated?.();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409 && error.code === "slot_taken") {
        setConflictBanner("Slot taken. Pick a different time, or use the walk-in override path.");
      } else {
        const message = error instanceof Error ? error.message : String(error);
        toast({ title: "Could not create appointment", description: message, variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New appointment</DialogTitle>
          <DialogDescription>Books directly without a hold (staff path, REQ-011).</DialogDescription>
        </DialogHeader>
        {conflictBanner && (
          <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive-foreground">
            {conflictBanner}
          </div>
        )}
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Field label="Customer">
            <Select
              value={form.customer_id}
              onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value }))}
            >
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Service">
            <Select
              value={form.service_id}
              onChange={(e) => setForm((f) => ({ ...f, service_id: e.target.value }))}
            >
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.duration_minutes}m)
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Resource">
            <Select
              value={form.primary_resource_id}
              onChange={(e) => setForm((f) => ({ ...f, primary_resource_id: e.target.value }))}
            >
              {resources.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Starts at">
            <Input
              type="datetime-local"
              value={form.starts_at ? toLocalDatetime(form.starts_at) : ""}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  starts_at: e.target.value ? new Date(e.target.value).toISOString() : ""
                }))
              }
            />
          </Field>
          <Field label="Notes">
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function toLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
